import React from 'react';
import { Control, Path, PathValue, UseFormSetValue, UseFormWatch, FieldValues } from 'react-hook-form';
import { FormField, FormItem, FormLabel, FormControl } from '@frontend/shadcn/components/ui/form';
import { Checkbox } from '@frontend/shadcn/components/ui/checkbox';
import { Input } from '@frontend/shadcn/components/ui/input';
import { Button } from '@frontend/shadcn/components/ui/button';
import { Card } from '@frontend/shadcn/components/ui/card';
import { PlusIcon, TrashIcon } from '@revfluence/fresh-icons/regular/esm';
import { GenericUtmField } from './generic-utm-field';

// Import the enforced type (avoiding circular import by using relative type import)
type DynamicOptions = Record<`{{${string}}}`, string>;

// Generic interface for custom UTM parameters
interface CustomUtmParameter {
  key?: string;
  value?: string;
}

// Configuration for field paths - allows flexible naming
interface UtmFieldPaths<TFieldValues> {
  useUtmParameters: Path<TFieldValues>;
  utmParameters: {
    utmSource: Path<TFieldValues>;
    utmMedium: Path<TFieldValues>;
    utmCampaign: Path<TFieldValues>;
    utmContent: Path<TFieldValues>;
    utmTerm: Path<TFieldValues>;
  };
  customUtmParameters: Path<TFieldValues>;
  targetUrl?: Path<TFieldValues>; // Optional field for the URL that will get UTMs appended
}

interface GenericUtmParametersGroupProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  watch: UseFormWatch<TFieldValues>;
  setValue: UseFormSetValue<TFieldValues>;
  fieldPaths: UtmFieldPaths<TFieldValues>;
  urlPreview?: string;
  showUrlPreview?: boolean;
  title?: string;
  description?: string;
  className?: string;
  dynamicOptions?: DynamicOptions; // Now enforces {{ }} format
}

export function GenericUtmParametersGroup<TFieldValues extends FieldValues>({
  control,
  watch,
  setValue,
  fieldPaths,
  urlPreview,
  showUrlPreview = true,
  title = 'UTM Parameters',
  description = 'Add UTM parameters to track the performance of your campaigns',
  className = '',
  dynamicOptions,
}: GenericUtmParametersGroupProps<TFieldValues>) {
  const useUtmParameters = watch(fieldPaths.useUtmParameters);
  const customUtmParameters = watch(fieldPaths.customUtmParameters) as CustomUtmParameter[] | undefined;

  const addCustomParameter = () => {
    const currentCustomParams = customUtmParameters || [];
    const newCustomParams = [...currentCustomParams, { key: '', value: '' }];
    setValue(
      fieldPaths.customUtmParameters,
      newCustomParams as PathValue<TFieldValues, typeof fieldPaths.customUtmParameters>,
    );
  };

  const removeCustomParameter = (index: number) => {
    const currentCustomParams = customUtmParameters || [];
    const newCustomParams = currentCustomParams.filter((_, i) => i !== index);
    setValue(
      fieldPaths.customUtmParameters,
      newCustomParams as PathValue<TFieldValues, typeof fieldPaths.customUtmParameters>,
    );
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* UTM Parameters Toggle */}
      <FormField
        control={control}
        name={fieldPaths.useUtmParameters}
        render={({ field }) => (
          <FormItem className="flex flex-row items-start space-x-3 space-y-0">
            <FormControl>
              <Checkbox checked={Boolean(field.value)} onCheckedChange={field.onChange} />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel className="text-sm font-medium">{title}</FormLabel>
              <p className="text-xs text-gray-600">{description}</p>
            </div>
          </FormItem>
        )}
      />

      {/* UTM Parameters Section */}
      {useUtmParameters && (
        <div className="space-y-4">
          {/* Standard UTM Parameters */}
          <Card className="p-4 space-y-4">
            <h4 className="text-sm font-medium text-gray-900">Standard UTM Parameters</h4>
            <div className="grid grid-cols-1 gap-4">
              <GenericUtmField
                control={control}
                name={fieldPaths.utmParameters.utmSource}
                label="UTM Source"
                placeholder="e.g. facebook, instagram, {{platform}}"
                utmType="source"
                dynamicOptions={dynamicOptions}
              />
              <GenericUtmField
                control={control}
                name={fieldPaths.utmParameters.utmMedium}
                label="UTM Medium"
                placeholder="e.g. social, cpc, paid"
                utmType="medium"
                dynamicOptions={dynamicOptions}
              />
              <GenericUtmField
                control={control}
                name={fieldPaths.utmParameters.utmCampaign}
                label="UTM Campaign"
                placeholder="e.g. {{adset.name}}, summer_sale"
                utmType="campaign"
                dynamicOptions={dynamicOptions}
              />
              <GenericUtmField
                control={control}
                name={fieldPaths.utmParameters.utmContent}
                label="UTM Content"
                placeholder="e.g. {{ad.name}}, banner_v1"
                utmType="content"
                dynamicOptions={dynamicOptions}
              />
              <GenericUtmField
                control={control}
                name={fieldPaths.utmParameters.utmTerm}
                label="UTM Term"
                placeholder="e.g. branded, {{creator.name}}"
                utmType="term"
                dynamicOptions={dynamicOptions}
              />
            </div>
          </Card>

          {/* Custom UTM Parameters */}
          <Card className="p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium text-gray-900">Custom UTM Parameters</h4>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addCustomParameter}
                className="flex items-center gap-1 h-8"
              >
                <PlusIcon className="h-3 w-3" />
                Add Parameter
              </Button>
            </div>

            {customUtmParameters && customUtmParameters.length > 0 ? (
              <div className="space-y-3">
                {customUtmParameters.map((_, index) => (
                  <div key={index} className="flex gap-2 items-end">
                    <FormField
                      control={control}
                      name={`${fieldPaths.customUtmParameters}.${index}.key` as Path<TFieldValues>}
                      render={({ field }) => (
                        <FormItem className="flex-1">
                          <FormLabel className="text-xs">Parameter Name</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              placeholder="e.g. affiliate_id"
                              value={String(field.value || '')}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                }
                              }}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={control}
                      name={`${fieldPaths.customUtmParameters}.${index}.value` as Path<TFieldValues>}
                      render={({ field }) => (
                        <FormItem className="flex-1">
                          <FormLabel className="text-xs">Parameter Value</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              placeholder="e.g. {{creator.handle}}, partner123"
                              value={String(field.value || '')}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                }
                              }}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => removeCustomParameter(index)}
                      className="h-8 w-8 flex-shrink-0"
                    >
                      <TrashIcon className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 italic">No custom parameters added yet.</p>
            )}
          </Card>

          {/* URL Preview */}
          {showUrlPreview && (
            <Card className="p-4">
              <h4 className="text-sm font-medium text-gray-900 mb-2">URL Preview</h4>
              <div className="bg-gray-50 p-3 rounded-md border">
                <p className="text-xs text-gray-600 mb-1">Final URL with UTM parameters:</p>
                <code className="text-xs bg-white p-2 rounded border block overflow-x-auto">
                  {urlPreview || 'Enter a destination URL to see the preview with UTMs.'}
                </code>
              </div>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
